"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { Menu, ChevronDown, X } from "lucide-react";
import { useLanguage } from "@/app/context/LanguageContext";

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();

  const galleryMenu = [
    {
      title: t.nav.imageGallery,
      href: "/gallery/image-gallery",
    },
    {
      title: t.nav.videoGallery,
      href: "/gallery/video-gallery",
    },
  ];

  return (
    <header className="sticky top-0 z-50 w-full  bg-[#140d07]/95 backdrop-blur-xl">
      <div className="max-w-[1400px] mx-auto h-20 px-4 lg:px-8 flex items-center justify-between">
        {/* LOGO */}
        <Link href="/" className="flex items-center gap-3">
          <Image
            src="/logo.png"
            alt="Sri 18m Padi Karuppanasamy Temple"
            width={70}
            height={70}
            className="h-14 w-auto object-contain"
            priority
          />
        </Link>

        {/* DESKTOP MENU */}
        <nav className="hidden lg:flex items-center gap-2">
          <Link
            href="/"
            className="px-4 py-2 rounded-full text-white hover:bg-white/10 transition"
          >
            {t.nav.home}
          </Link>

          <Link
            href="/about"
            className="px-4 py-2 rounded-full text-white hover:bg-white/10 transition"
          >
            {t.nav.about}
          </Link>

          <Link
            href="/pooja-schedule"
            className="px-4 py-2 rounded-full text-white hover:bg-white/10 transition"
          >
            {t.nav.poojaSchedule}
          </Link>

          <Link
            href="/events"
            className="px-4 py-2 rounded-full text-white hover:bg-white/10 transition"
          >
            {t.nav.events}
          </Link>

          {/* GALLERY DROPDOWN */}
          <div className="relative group">
            <button className="flex items-center gap-1 px-4 py-2 rounded-full text-white hover:bg-white/10 transition">
              {t.nav.gallery}
              <ChevronDown className="h-4 w-4" />
            </button>

            <div className="absolute top-full left-0 pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
              <div className="w-56 rounded-2xl border border-white/10 bg-[#1B120A] shadow-2xl p-2">
                {galleryMenu.map((item) => (
                  <Link
                    key={item.title}
                    href={item.href}
                    className="block rounded-xl px-4 py-3 text-sm text-white hover:bg-white/10 hover:text-amber-300 transition"
                  >
                    {item.title}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <Link
            href="/contact"
            className="px-4 py-2 rounded-full text-white hover:bg-white/10 transition"
          >
            {t.nav.contact}
          </Link>

          {/* LANGUAGE SWITCHER DESKTOP */}
          <button
            onClick={() => setLanguage(language === "en" ? "ta" : "en")}
            className="ml-2 px-3 py-1.5 rounded-full border border-amber-500/30 text-amber-300 hover:bg-amber-500/10 transition text-sm font-semibold"
          >
            {language === "en" ? "தமிழ்" : "EN"}
          </button>
        </nav>

        {/* RIGHT BUTTON */}
        <div className="hidden lg:flex items-center">
          <Link
            href="/contact"
            className="
              px-6
              h-11
              rounded-full
              flex
              items-center
              justify-center
              font-semibold
              text-white
              bg-gradient-to-r
              from-amber-600
              via-orange-500
              to-red-500
              hover:scale-105
              transition-all
              duration-300
              shadow-lg
              shadow-orange-500/20
            "
          >
            {t.headerButton}
          </Link>
        </div>

        {/* MOBILE ACTIONS */}
        <div className="flex lg:hidden items-center gap-2">
          {/* LANGUAGE SWITCHER MOBILE */}
          <button
            onClick={() => setLanguage(language === "en" ? "ta" : "en")}
            className="px-3 py-1.5 rounded-full border border-amber-500/40 text-amber-200 bg-amber-500/10 hover:bg-amber-500/20 transition text-xs font-bold backdrop-blur-sm"
          >
            {language === "en" ? "தமிழ்" : "EN"}
          </button>

          <button
            onClick={() => setMobileMenuOpen(true)}
            className="h-10 w-10 sm:h-11 sm:w-11 rounded-full bg-white/15 flex items-center justify-center text-white border border-white/10 hover:bg-white/20 transition-colors"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* MOBILE MENU */}
      <div
        className={`fixed inset-0 z-[999] bg-[#0a0a0a] w-full h-screen transition-all duration-300 ${mobileMenuOpen ? "visible opacity-100" : "invisible opacity-0"
          }`}
      >
        {/* OVERLAY */}
        <div
          className="absolute inset-0 bg-black"
          onClick={() => setMobileMenuOpen(false)}
        />

        {/* SIDEBAR */}
        <div
          className={`absolute right-0 top-0 h-full w-[85%] max-w-[340px] bg-[#140d07] border-l border-white/10 transition-transform duration-300 ${mobileMenuOpen ? "translate-x-0" : "translate-x-full"
            }`}
        >
          {/* TOP */}
          <div className="flex items-center justify-between p-5 border-b border-white/10">
            <div>
              <h2 className="text-white font-bold text-lg">{t.titleMain}</h2>

              <p className="text-amber-400 text-sm">{t.titleSub}</p>
            </div>

            <button
              onClick={() => setMobileMenuOpen(false)}
              className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* MOBILE LINKS */}
          <div className="p-5 flex flex-col gap-3 overflow-y-auto">
            <MobileLink
              href="/"
              label={t.nav.home}
              onClick={() => setMobileMenuOpen(false)}
            />

            <MobileLink
              href="/about"
              label={t.nav.about}
              onClick={() => setMobileMenuOpen(false)}
            />

            <MobileLink
              href="/pooja-schedule"
              label={t.nav.poojaSchedule}
              onClick={() => setMobileMenuOpen(false)}
            />

            <MobileLink
              href="/events"
              label={t.nav.events}
              onClick={() => setMobileMenuOpen(false)}
            />

            {/* MOBILE GALLERY */}
            <div className="border border-white/10 rounded-2xl overflow-hidden">
              <button
                onClick={() => setGalleryOpen(!galleryOpen)}
                className="w-full flex items-center justify-between px-4 py-4 text-white bg-white/5"
              >
                <span className="font-medium">{t.nav.gallery}</span>

                <ChevronDown
                  className={`h-4 w-4 transition-transform duration-300 ${galleryOpen ? "rotate-180" : ""
                    }`}
                />
              </button>

              {galleryOpen && (
                <div className="border-t border-white/10 bg-[#1B120A] px-2 py-2 space-y-1">
                  {galleryMenu.map((item) => (
                    <Link
                      key={item.title}
                      href={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block pl-8 pr-4 py-3 rounded-xl text-sm text-white hover:bg-white/10 transition"
                    >
                      {item.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <MobileLink
              href="/contact"
              label={t.nav.contact}
              onClick={() => setMobileMenuOpen(false)}
            />
          </div>

          {/* BOTTOM BUTTON */}
          <div className="p-5 border-t border-white/10 mt-auto">
            <Link
              href="/contact"
              onClick={() => setMobileMenuOpen(false)}
              className="
                w-full
                h-12
                rounded-full
                flex
                items-center
                justify-center
                text-white
                font-semibold
                bg-gradient-to-r
                from-amber-600
                via-orange-500
                to-red-500
              "
            >
              {t.headerButton}
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}

/* MOBILE LINK */

function MobileLink({
  href,
  label,
  onClick,
}: {
  href: string;
  label: string;
  onClick?: () => void;
}) {
  return (
    <Link
      href={href}
      onClick={onClick}
      className="
        px-4
        py-4
        rounded-full
        border
        border-white/10
        text-white
        hover:bg-white/10
        transition
      "
    >
      {label}
    </Link>
  );
}
